import { AspectRatio, Button, Divider, Flex, Heading, Hide, Stack } from '@chakra-ui/react'
import { useQuery } from '@tanstack/react-query'
import type { ComponentCampaignCurrentNft } from '@twlvxtwlv/cms'
import type { CurrentNftWithProduct } from '@twlvxtwlv/mapping'
import type { AuctionWithHighestBid } from '@twlvxtwlv/supabase'
import { getPreviewUrlByProductId } from '@twlvxtwlv/supabase'
import type { ProductVariant } from '@twlvxtwlv/tokenforge'
import {
  getMediaFileUrl,
  getPriceInEurocentsForProduct,
  getProductAvailability,
  getProductType,
  type Product,
} from '@twlvxtwlv/tokenforge'
import type { ProductPurchasbility } from '@twlvxtwlv/types'
import { useTranslation } from 'next-i18next'
import Image from 'next/image'
import { useEffect, useState } from 'react'
import { Video } from '../../../../../shared/video'
import { AuctionCta } from '../cta/auction-cta'
import { ProductsLeftCta } from '../cta/products-left-cta'
import { TokenContent } from '../token-content'

export interface ProductDetailsProps {
  auctionsWithHighestBid: AuctionWithHighestBid[]
  campaignId: string | null
  currentNftWithProduct: CurrentNftWithProduct
  onOpenNotifyMeModal: () => void
  onTrackLearnMoreButtonClicked: (
    currentNft: ComponentCampaignCurrentNft,
    priceInEuroCents: number
  ) => void
  onTrackPurchaseButtonClicked: (
    currentNft: ComponentCampaignCurrentNft,
    priceInEuroCents: number
  ) => void
}

export function ProductDetails({
  auctionsWithHighestBid,
  campaignId,
  currentNftWithProduct,
  onOpenNotifyMeModal,
  onTrackLearnMoreButtonClicked,
  onTrackPurchaseButtonClicked,
}: ProductDetailsProps) {
  const { t } = useTranslation(['common', 'campaign'])
  const [productPurchasability, setProductPurchasability] = useState<ProductPurchasbility | null>(
    null
  )

  const { data: videoUrl } = useQuery({
    queryKey: ['campaign-preview-urls', { productId: currentNftWithProduct.product.code }],
    queryFn: () => getPreviewUrlByProductId(currentNftWithProduct.product.code),
  })

  const [amountOfContentShown, setAmountOfContentShown] = useState(2)
  const productType = getProductType(currentNftWithProduct.product)

  const variant: ProductVariant | undefined | null = currentNftWithProduct.product?.defaultVariant

  useEffect(() => {
    const product = currentNftWithProduct.product
    if (!product) return
    setProductPurchasability(getProductAvailability(product, variant?.onHand ?? 0))
  }, [currentNftWithProduct, variant])

  const currentNft: ComponentCampaignCurrentNft = currentNftWithProduct.currentNft
  const product: Product = currentNftWithProduct.product

  let priceInEuroCents = -1
  if (variant) {
    priceInEuroCents = getPriceInEurocentsForProduct(product)
  }

  let auctionWithHighestBid: AuctionWithHighestBid | undefined = undefined
  auctionWithHighestBid = auctionsWithHighestBid?.find((auction) => {
    return auction.tfProductId === currentNft.nftId
  })

  if (!currentNft) {
    return null
  }

  const nftImageUrl = getMediaFileUrl(product, 'gallery')
  const nftImageAlt = `${product?.name} product Image`

  function showMoreClickHandler() {
    if (currentNft?.contents) {
      setAmountOfContentShown(currentNft.contents.length)
    }
  }

  return (
    <Stack overflow="hidden" w="full" flexGrow="1" h="full">
      {currentNft.contents ? (
        <>
          <Stack spacing={0} gap={12} w="full" h="full">
            {videoUrl ? (
              <Stack gap="4" w="full" justify="start" direction="column">
                <Heading as="h2" fontSize="2xl">
                  {t('campaign:pre-listen-heading')}
                </Heading>
                <AspectRatio w="full" maxW="400px" ratio={16 / 9}>
                  <Video url={videoUrl} />
                </AspectRatio>
              </Stack>
            ) : (
              <Stack spacing={0} gap={4}>
                <Flex direction={{ base: 'column', xl: 'row' }} gap={4} w="full">
                  <Stack w="full" spacing={0} flex={1} gap={4}>
                    <Heading as="h2" textTransform="uppercase" fontSize="xl">
                      {currentNft.headline}
                    </Heading>
                    <Hide below="xl">
                      <Heading
                        as="h3"
                        fontSize={{ base: 'sm', md: 'md' }}
                        textTransform="uppercase"
                      >
                        {currentNft.subHeadline}
                      </Heading>
                    </Hide>
                  </Stack>

                  {nftImageUrl ? (
                    <AspectRatio maxW="400px" ratio={1} flex={1}>
                      <Image
                        src={nftImageUrl}
                        alt={nftImageAlt}
                        width={400}
                        height={400}
                        style={{
                          objectFit: 'contain',
                        }}
                      />
                    </AspectRatio>
                  ) : null}
                </Flex>

                <Hide above="xl">
                  <Heading as="h3" fontSize={{ base: 'sm', md: 'md' }} textTransform="uppercase">
                    {currentNft.subHeadline}
                  </Heading>
                </Hide>
              </Stack>
            )}

            <Stack gap={12} h="full">
              {currentNft.contents
                .filter(Boolean)
                .slice(0, amountOfContentShown)
                .map((content, index) => {
                  return (
                    <TokenContent
                      key={content?.id}
                      description={content?.description ?? undefined}
                      headline={content?.headline ?? undefined}
                      subHeadline={`${index + 1}/${currentNft.contents?.length ?? 0}`}
                      imageAlt={content?.imageAlt ?? undefined}
                      imageUrl={content?.imageUrl?.data?.attributes?.url}
                    />
                  )
                })}
            </Stack>
          </Stack>
        </>
      ) : null}

      {currentNft?.contents?.length && currentNft?.contents?.length > amountOfContentShown ? (
        <Flex justifyContent="center" alignItems="center">
          <Button onClick={showMoreClickHandler} variant="txtBlack">
            {t('show-more')}
          </Button>
        </Flex>
      ) : null}

      <Flex
        direction="column"
        flexGrow={[0, 0, 0, 0, 0]}
        flexShrink={[0, 0, 0, 0, 0]}
        gap={3}
        w="full"
        pt="4"
      >
        {product && productPurchasability?.isListable ? (
          <>
            <Divider borderWidth="1px" borderColor="#000" opacity={100} />
            {auctionWithHighestBid ? (
              <AuctionCta auctionWithHighestBid={auctionWithHighestBid} />
            ) : (
              <ProductsLeftCta
                campaignId={campaignId}
                currentNft={currentNft}
                variant={variant}
                availability={productPurchasability.availability}
                productIsPurchasable={productPurchasability.isPurchasable}
                priceInEuroCents={priceInEuroCents}
                onPurchaseButtonClicked={() =>
                  onTrackPurchaseButtonClicked(currentNft, priceInEuroCents)
                }
                onLearnMoreButtonClicked={() =>
                  onTrackLearnMoreButtonClicked(currentNft, priceInEuroCents)
                }
                productType={productType}
                onOpenNotifyMeModal={onOpenNotifyMeModal}
              />
            )}
          </>
        ) : null}
      </Flex>
    </Stack>
  )
}
